import { useState } from 'react';
import { Button, Dialog, Drawer, Sortable, Switch, ThreeDotPopover } from '@dreamtree-org/twreact-ui';
import { GripVertical, Plus, Copy, Trash2, Pencil, MoreVertical } from 'lucide-react';
import { TenantPage } from '@dreamtree-org/landing-kit';
import { SECTION_TYPES, SECTION_TYPE_KEYS, CHROME_FIELDS, CARD_FIELDS } from '@dreamtree-org/landing-kit/schema';
import '@dreamtree-org/landing-kit/styles.css';
import StorePreview from '@/storefront/design/StorePreview';
import { usePhone } from '@/lib/useMediaQuery';
import { Fields } from './FieldEditor';
import ThemePanel from './ThemePanel';
import TemplatePanel from './TemplatePanel';
import { OPTION_TILE } from './optionTile';
import { PreviewLink, PREVIEW_SLOTS } from './previewParts';
import {
  addSection, duplicateSection, removeSection, updateSection, sectionLabel, sectionTypeLabel, errorsBySection, setHome, setChrome, setCard,
} from '../../lib/appearance';

/**
 * Storefront Studio areas (B15): each is one registry entry — the editor panel,
 * the preview, where "Preview" opens on the storefront, and the preview caption.
 * A new area is an entry here plus its label in lib/appearance STUDIO_AREAS.
 *
 * Panel props: { doc, theme, onDoc(doc), onTheme(theme), byArea, saveErrors, editable, draftId }.
 * Preview props: { area, doc, theme, media, catalog: { listings, types, categories, name } }.
 */


const MAX_SECTIONS = 20;
/** The landing page: sections the owner adds, orders, hides and edits (D24). */
function HomePanel({ doc, onDoc, byArea, editable, draftId }) {
  const page = doc.pages.home;
  const bySection = errorsBySection(page, byArea.home);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const phone = usePhone();
  const changePage = (next) => onDoc(setHome(doc, next));
  const editingSection = editing && page.sections.find((s) => s.id === editing);
  const full = page.sections.length >= MAX_SECTIONS;
  return (
    <div className="space-y-3">
      <Sortable
        items={page.sections}
        getKey={(s) => s.id}
        getItemLabel={(s) => sectionLabel(s)}
        handle
        disabled={!editable}
        onReorder={(sections) => changePage({ ...page, sections })}
        renderItem={(s, { handleProps }) => {
          const problems = bySection[s.id];
          return (
            <div className={`flex items-center gap-2 rounded-lg border p-2 ${problems ? 'border-error-400' : 'border-[var(--app-border)]'} ${s.enabled ? '' : 'opacity-60'}`}>
              <span {...handleProps} className={`${handleProps.className || ''} cursor-grab rounded p-1 opacity-60 hover:opacity-100`} role="button" tabIndex={0} aria-label={`Move ${sectionLabel(s)}`}>
                <GripVertical className="h-4 w-4" aria-hidden="true" />
              </span>
              <button type="button" className="min-w-0 flex-1 text-left" onClick={() => setEditing(s.id)}>
                <span className="block truncate text-sm font-medium">{sectionLabel(s)}</span>
                <span className="block text-xs opacity-60">{sectionTypeLabel(s.type)}{problems ? ` · ${problems.length} to fix` : ''}</span>
              </button>
              <Switch checked={s.enabled} onChange={(on) => changePage(updateSection(page, s.id, { enabled: on }))} ariaLabel={`Show ${sectionLabel(s)}`} disabled={!editable} size="sm" />
              <ThreeDotPopover
                ariaLabel={`Actions for ${sectionLabel(s)}`}
                trigger={<Button size="icon-sm" variant="ghost" aria-label={`Actions for ${sectionLabel(s)}`}><MoreVertical className="h-4 w-4" aria-hidden="true" /></Button>}
                items={[
                  { key: 'edit', label: 'Edit', icon: <Pencil className="h-4 w-4" aria-hidden="true" />, onClick: () => setEditing(s.id) },
                  { key: 'dup', label: 'Duplicate', icon: <Copy className="h-4 w-4" aria-hidden="true" />, disabled: !editable || full, onClick: () => changePage(duplicateSection(page, s.id)) },
                  { key: 'del', label: 'Delete', tone: 'destructive', icon: <Trash2 className="h-4 w-4" aria-hidden="true" />, disabled: !editable, onClick: () => changePage(removeSection(page, s.id)) },
                ]}
              />
            </div>
          );
        }}
      />
      {!page.sections.length ? <p className="text-sm opacity-70">No sections yet. Add a hero slider to start.</p> : null}
      <Button variant="outline" fullWidth leftIcon={<Plus className="h-4 w-4" />} onClick={() => setAdding(true)} disabled={!editable || full}>Add section</Button>

      <Drawer
        isOpen={!!editingSection}
        onClose={() => setEditing(null)}
        title={editingSection ? sectionTypeLabel(editingSection.type) : ''}
        description={editingSection ? SECTION_TYPES[editingSection.type].description : ''}
        placement={phone ? 'bottom' : 'right'}
        size={phone ? 'full' : 'lg'}
        swipeToClose
        footer={<div className="flex justify-end"><Button onClick={() => setEditing(null)}>Done</Button></div>}
      >
        {editingSection ? (
          <fieldset disabled={!editable} className="space-y-4">
            <Fields
              fields={SECTION_TYPES[editingSection.type].fields}
              value={editingSection.props}
              errors={bySection[editingSection.id] || []}
              draftId={draftId}
              onChange={(props) => changePage(updateSection(page, editingSection.id, { props }))}
            />
          </fieldset>
        ) : null}
      </Drawer>

      <Dialog isOpen={adding} onClose={() => setAdding(false)} title="Add a section" size="lg">
        <div className="grid gap-2 sm:grid-cols-2">
          {SECTION_TYPE_KEYS.map((type) => (
            <button key={type} type="button" className={OPTION_TILE}
              onClick={() => {
                const r = addSection(page, type);
                changePage(r.page);
                setAdding(false);
                setEditing(r.section.id);
              }}>
              <span className="block text-sm font-semibold">{SECTION_TYPES[type].label}</span>
              <span className="block text-xs opacity-70">{SECTION_TYPES[type].description}</span>
            </button>
          ))}
        </div>
      </Dialog>
    </div>
  );
}

const templatePanel = (pageKey) => function PagePanel({ doc, onDoc, byArea, editable, draftId }) {
  return <TemplatePanel pageKey={pageKey} doc={doc} onChange={onDoc} errors={byArea[pageKey]} editable={editable} draftId={draftId} />;
};

function ChromePanel({ doc, onDoc, byArea, editable, draftId }) {
  return (
    <fieldset disabled={!editable} className="space-y-6">
      {CHROME_FIELDS.map((g) => (
        <section key={g.key} className="space-y-3">
          <h3 className="text-sm font-semibold">{g.label}</h3>
          <Fields fields={g.fields} value={doc.chrome[g.key]} path={g.key} errors={byArea.chrome} draftId={draftId}
            onChange={(v) => onDoc(setChrome(doc, { ...doc.chrome, [g.key]: v }))} />
        </section>
      ))}
      <p className="text-xs opacity-60">Leave the menu links or footer columns empty to keep the store's own (repair, warranty, sell; shop, services, account).</p>
    </fieldset>
  );
}

function CardPanel({ doc, onDoc, byArea, editable }) {
  return (
    <fieldset disabled={!editable} className="space-y-3">
      <p className="text-sm opacity-70">The product card is used in listings, on the home page and under "similar products".</p>
      <Fields fields={CARD_FIELDS} value={doc.card} errors={byArea.card} onChange={(card) => onDoc(setCard(doc, card))} />
    </fieldset>
  );
}

function ThemeArea({ theme, onTheme, saveErrors, draftId }) {
  return <ThemePanel theme={theme} onChange={onTheme} draftId={draftId} errors={saveErrors} />;
}

function LandingPreview({ doc, theme, media }) {
  return (
    <TenantPage page={doc.pages.home} theme={theme} storeName="" resolveMedia={media} linkComponent={PreviewLink} slots={PREVIEW_SLOTS} headingStart={2}
      onSubscribe={() => Promise.reject(new Error('Sign-ups work on the live storefront.'))} />
  );
}

function StorePagePreview({ area, doc, theme, media, catalog }) {
  return <StorePreview area={area} doc={doc} theme={theme} listings={catalog.listings} types={catalog.types} categories={catalog.categories} name={catalog.name} mediaUrl={media} />;
}

const LANDING_CAPTION = 'Featured products and categories fill in from your catalog on the storefront.';
const STORE_CAPTION = 'Shown with your published products and a sample inspection report.';

/** Area → { Panel, Preview, path(listingId), caption }. */
export const STUDIO_PANELS = {
  home: { Panel: HomePanel, Preview: LandingPreview, path: () => '/', caption: LANDING_CAPTION },
  listing: { Panel: templatePanel('listing'), Preview: StorePagePreview, path: () => '/shop', caption: STORE_CAPTION },
  product: { Panel: templatePanel('product'), Preview: StorePagePreview, path: (id) => (id ? `/p/${id}` : '/shop'), caption: STORE_CAPTION },
  chrome: { Panel: ChromePanel, Preview: StorePagePreview, path: () => '/shop', caption: STORE_CAPTION },
  card: { Panel: CardPanel, Preview: StorePagePreview, path: () => '/shop', caption: STORE_CAPTION },
  theme: { Panel: ThemeArea, Preview: LandingPreview, path: () => '/', caption: LANDING_CAPTION },
};
